import { FC, ComponentProps, useState, useContext, useEffect } from 'react';

// context
import { getSpaceSearch } from 'hooks/useSpaceSearch';
import useApiRequest from 'hooks/useApiRequest';
import { AuthContext } from '../../state/auth';

import { MAXIMUM_SPACES_PER_BOOKING } from '../../constants/bookings';

// components
import SpaceListItem from './item';
import Modals, { ModalType } from './modals';

interface Image {
  regular: string;
  thumbnail: string;
}

interface Space {
  id: number;
  name: string;
  image: Image;
  clinical: boolean;
  priceIncVat: number;
  capacity: number;
  floor: string;
  floorplan: string;
  facets: any[];
}

interface Venue {
  id: number;
  name: string;
}

interface Props {
  spaces: Space[];
  venue: Venue;
  selectedVenueId?: number;
  selectedSpace?: Space;
  selectedSpaceIds: number[];
  onAddToBooking: (space: Space, venue: Venue) => any;
  spaceServiceClash?: (space: Space) => boolean;
  limitSpaceSelections?: boolean;
  fetchSpaces?: boolean;
  linkComponent?: ComponentProps<typeof SpaceListItem>['linkComponent'];
}

const SpaceList: FC<Props> = ({
  spaces = [],
  venue = {},
  selectedVenueId,
  selectedSpace,
  selectedSpaceIds = [],
  onAddToBooking = () => {},
  spaceTypeValid = () => true,
  spaceServiceClash = () => false,
  limitSpaceSelections = true,
  fetchSpaces = true,
  linkComponent,
}) => {
  const { loggedIn, accountApproved } = useContext(AuthContext);

  const [openModal, setOpenModal] = useState<ModalType | null>(null);
  const [spaceData, setSpaceData] = useState<Space[] | null>(null);

  const closeModal = () => setOpenModal(null);

  const apiRequest = useApiRequest();

  useEffect(() => {
    async function fetchSpaceData() {
      if (fetchSpaces) {
        const fetchedVenue = await getSpaceSearch(apiRequest, {
          filters: {
            selectedVenueIds: [venue.id],
          },
        });

        return setSpaceData(fetchedVenue.venues[0]?.spaces);
      }

      return setSpaceData(spaces);
    }

    fetchSpaceData();
  }, [venue, apiRequest, fetchSpaces, spaces]);

  const handleAddToBookingClick = (space, venue, serviceClash, typeValid) => {
    if (!typeValid) {
      return setOpenModal(
        space.clinical
          ? 'space-type-clinical-error'
          : 'space-type-non-clinical-error'
      );
    }

    if (serviceClash) {
      return setOpenModal('service-clash');
    }

    if (!loggedIn) {
      return setOpenModal('sign-in');
    }
    if (!accountApproved) {
      return setOpenModal('unapproved-user-warning');
    }

    if (
      limitSpaceSelections &&
      selectedSpaceIds.length === MAXIMUM_SPACES_PER_BOOKING
    ) {
      return setOpenModal('space-count-warning');
    }

    if (selectedVenueId && selectedVenueId !== venue.id) {
      return setOpenModal('add-to-booking-failure');
    }

    return onAddToBooking(space, venue);
  };

  return (
    <>
      <ul className="venue__spaces">
        {spaceData?.map(space => {
          const serviceClash = spaceServiceClash(space);
          const typeValid = spaceTypeValid(space);

          return (
            <SpaceListItem
              key={space.id}
              facets={space.facets}
              selected={!!selectedSpace}
              disabled={serviceClash || !typeValid}
              discount={space.discount}
              discounts={space.discounts}
              addedToBooking={selectedSpaceIds.includes(space.id)}
              id={space.id}
              image={space.image?.thumbnail}
              name={space.name}
              venue={venue}
              clinical={space.clinical}
              price={Number(space.priceIncVat)}
              capacity={space.capacity}
              floor={space.floor}
              floorplan={space.floorplan}
              handleAddToBookingClick={() =>
                handleAddToBookingClick(space, venue, serviceClash, typeValid)
              }
              linkComponent={linkComponent}
              categoryIds={space.categoryIds}
            />
          );
        })}
      </ul>
      <Modals openModal={openModal} onClose={closeModal} />
    </>
  );
};

export default SpaceList;
